/**
 * React access to the replica.
 *
 * Components subscribe to the store's version counter and then read the replica directly.
 * A single subscription per component keeps the wiring small and guarantees that every
 * screen sees the same data in the same render pass.
 */

import { createContext, useContext, useSyncExternalStore } from 'react';

import type { AppStore } from './store.js';

const StoreContext = createContext<AppStore | null>(null);

export const StoreProvider = StoreContext.Provider;

/**
 * Returns the application store.
 *
 * @throws Error when called outside `StoreProvider`, which is a wiring mistake.
 */
export function useStore(): AppStore {
  const store = useContext(StoreContext);
  if (store === null) throw new Error('useStore must be used inside StoreProvider');
  return store;
}

/** Subscribes to replica changes and returns the current version counter. */
export function useReplicaVersion(): number {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, store.getVersion, store.getVersion);
}
